/**
 * @file I18nProvider.tsx
 * @description Mounts the app-wide i18next instance over the tree.
 * @module i18n
 */
import React from "react";
import { I18nextProvider } from "react-i18next";

import { i18n } from "./instance";

import type { i18n as I18nInstance } from "i18next";
import type { PropsWithChildren } from "react";

/**
 * Provides the i18next instance to the tree.
 *
 * This provider stays mounted in every kill-switch state. Unmounting it is what
 * makes call sites render raw keys, which is the failure the switch exists to
 * avoid; "off" is expressed in the instance's options instead (see
 * `buildInitOptions`).
 * @param props - Component props
 * @param props.children - The tree to provide translations to
 * @param props.instance - The instance to provide; defaults to the app-wide one
 * @returns The provider element
 */
export function I18nProvider({
  children,
  instance = i18n,
}: PropsWithChildren<{ readonly instance?: I18nInstance }>) {
  return <I18nextProvider i18n={instance}>{children}</I18nextProvider>;
}
